<!-- 可裁剪的上传控件 -->
<template>
	<div class="image-cropper-container">
		<div class="input-upload" v-if="props.isShowInput">
			<el-input v-model="imageUrl" placeholder="请输入文件路径地址" autocomplete="off" :onChange="handleChange" />
		</div>
		<div class="image-show" v-if="props.isShowImage">
			<el-image class="pic" :src="imageUrl" />
		</div>
		<el-upload class="image-uploader" :show-file-list="false" :before-upload="beforeUpload" accept="image/*">
			<el-button type="primary">{{ props.btnTitle }}</el-button>
		</el-upload>
	</div>
	<!-- 图片预览裁剪 -->
	<el-dialog v-model="showCaptureVisible" title="图片预览裁剪" width="50%" @close="closeCapture()" draggable destroy-on-close>
		<div class="w-full h-78vh mt-10px flex justify-center items-center">
			<!-- 裁剪区域 -->
			<div v-if="imageSrc" class="cropper-wrapper">
				<vue-cropper
					ref="cropperRef"
					:imgFormat="'png'"
					:outputType="'png'"
					:img="imageSrc"
					:auto-crop="options.autoCrop"
					:auto-crop-width="options.autoCropWidth"
					:auto-crop-height="options.autoCropHeight"
					:fixed="props.isFixed"
					:fixed-number="options.fixedNumber"
					:center-box="options.centerBox"
					:high="options.high"
					:mode="options.mode"
					@wheel="handleWheel"
					@realTime="onRealTime"></vue-cropper>

				<!-- 预览区域 -->
				<div class="preview-wrapper">
					<div class="preview-title">图片预览</div>
					<div
						class="preview-img"
						:style="{
							width: preview.w + 'px',
							height: preview.h + 'px',
							overflow: 'hidden',
							margin: '5px'
						}">
						<div :style="preview.div">
							<img :src="preview.url" :style="preview.img" />
						</div>
					</div>
					<div class="preview-btn">
						<div class="up-set">
							<el-slider v-model="sliderNum" style="width: 100%" :min="0" :max="maxValue" @input="handleSliderInput" @change="handleSliderChange"></el-slider>
							<div class="fix-input">当前尺寸：{{ roundFormat(preview.w) }} X {{ roundFormat(preview.h) }}</div>
						</div>
						<div class="up-btn">
							<el-upload :show-file-list="false" :before-upload="beforeUpload" accept="image/*">
								<el-button type="success">
									<el-icon class="mr-3px"><Upload /></el-icon>
									重新选择
								</el-button>
							</el-upload>
						</div>
					</div>
				</div>
			</div>

			<!-- 操作按钮 -->
			<div v-if="imageSrc" class="action-buttons">
				<div style="width: 60%; text-align: center">
					<el-button @click="rotateLeft()">
						<el-icon><RefreshLeft /></el-icon>
						左旋转
					</el-button>
					<el-button @click="rotateRight()">
						<el-icon><RefreshRight /></el-icon>
						右旋转
					</el-button>
					<el-button @click="zoomIn()">
						<el-icon><ZoomIn /></el-icon>
						放大
					</el-button>
					<el-button @click="zoomOut()">
						<el-icon><ZoomOut /></el-icon>
						缩小
					</el-button>
				</div>
				<div style="margin-left: 20px">
					<el-button type="primary" @click="handleCropperUpload()">
						<el-icon><Upload /></el-icon>
						裁剪上传
					</el-button>
					<el-button type="primary" @click="handleOriginalUpload()" v-if="props.isOriginal">
						<el-icon><Upload /></el-icon>
						原图上传
					</el-button>
				</div>
			</div>
		</div>
		<template #footer>
			<span class="dialog-footer">
				<el-button type="info" @click="closeCapture()">关闭</el-button>
			</span>
		</template>
	</el-dialog>
</template>

<script setup>
import { ref, reactive, nextTick } from 'vue'
import { ElMessage } from 'element-plus'
import { ZoomIn, ZoomOut, RefreshLeft, RefreshRight, Refresh, Upload } from '@element-plus/icons-vue'

import { roundFormat } from '@/utils/index'
import { checkFileUpload } from '@/utils/scm'
import { UploadFilesByBase64, uploadCropperImages } from '@/api/upload'
import { useImageUrl } from '@/utils/dynamic'
import defaultImgs from '@/images'

import { VueCropper } from 'vue-cropper'
import 'vue-cropper/dist/index.css'

const emit = defineEmits(['update:modelValue', 'cropped'])

const props = defineProps({
	// 父组件 v-model 没有指定参数名，则默认是 modelValue
	modelValue: [String, Object, Array],
	// 大小限制(MB)
	fileSize: {
		type: Number,
		default: 5
	},
	// 文件类型, 例如['png', 'jpg', 'jpeg']
	fileType: {
		type: Array,
		default: () => ['png', 'jpg', 'jpeg', 'gif']
	},
	// 是否显示输入框
	isShowInput: {
		type: Boolean,
		default: true
	},
	// 是否显示图片
	isShowImage: {
		type: Boolean,
		default: true
	},
	// 按钮标题文字
	btnTitle: {
		type: String,
		default: '裁剪上传'
	},
	// 是否固定比例
	isFixed: {
		type: Boolean,
		default: true
	},
	// 是否可传原图
	isOriginal: {
		type: Boolean,
		default: true
	}
})

const { proxy } = getCurrentInstance()

const imageUrl = ref(defaultImgs.defaultImg)

const zoomNum = ref(1)
const maxZoom = ref(5)

const sliderVal = ref(100)
const sliderNum = ref(0)
const maxValue = ref(100) // 最大值，可以根据需要调整

const imageSrc = ref('') // 图片源
const cropperRef = ref(null) // 获取cropper实例
const showCaptureVisible = ref(false)
const imageFileType = ref('image/jpeg')

// 预览数据
const preview = reactive({
	w: 200,
	h: 200,
	div: {},
	url: '',
	img: {}
})
// 配置选项
const options = reactive({
	autoCrop: true, // 是否默认生成截图框
	autoCropWidth: 300, // 默认生成截图框宽度
	autoCropHeight: 300, // 默认生成截图框高度
	//fixed: false, // 是否开启截图框宽高固定比例
	fixedNumber: [1, 1], // 截图框比例 [宽度, 高度]
	centerBox: true, // 截图框是否被限制在图片里面
	high: true, // 是否按照设备的dpr 输出等比例图片
	mode: 'contain' // 图片默认渲染方式
})

//上传成功, 数据双向绑定
function handleUploadSuccess(res) {
	if (res && res.ResData) {
		imageUrl.value = res.ResData.fileUrl
		emit('update:modelValue', imageUrl.value)
	} else {
		ElMessage.error(res.ErrMsg)
	}
}
//更新对象值
function handleChange(value) {
	if (value != props.modelValue) {
		imageUrl.value = useImageUrl(value)
		emit('update:modelValue', imageUrl.value)
	}
}

const validateImage = (url) => {
	return new Promise((resolve) => {
		const img = new Image()
		img.onload = () => resolve(true)
		img.onerror = () => resolve(false)
		img.src = url
	})
}
// 上传前处理
const beforeUpload = async (file) => {
	imageFileType.value = file.type
	//使用 before-upload 限制用户上传的图片格式和大小
	var flag = checkFileUpload(file, props.fileType, props.fileSize)
	if (!flag) {
		return
	}
	const reader = new FileReader()
	reader.readAsDataURL(file)
	reader.onload = (e) => {
		const isValid = validateImage(e.target.result)
		if (isValid) {
			nextTick(() => {
				imageSrc.value = e.target.result
				showCaptureVisible.value = true
			})
		} else {
			ElMessage.error('无效的图片文件')
		}
	}
	return false // 阻止自动上传
}

// 实时预览函数
const onRealTime = (data) => {
	var w = roundFormat(data.w)
	var h = roundFormat(data.h)

	preview.w = w
	preview.h = h

	preview.div = {
		width: w + 'px',
		height: h + 'px',
		overflow: 'hidden',
		margin: '0 auto'
	}
	preview.url = data.url
	preview.img = {
		width: data.img.width + 'px',
		height: data.img.height + 'px',
		transform: data.img.transform
	}
}
// 向左旋转
const rotateLeft = () => {
	cropperRef.value.rotateLeft()
}
// 向右旋转
const rotateRight = () => {
	cropperRef.value.rotateRight()
}
// 放大
const zoomIn = () => {
	sliderNum.value += zoomNum.value
	cropperRef.value.changeScale(zoomNum.value)
}
// 缩小
const zoomOut = () => {
	if (sliderNum.value > 0) {
		sliderNum.value -= zoomNum.value
	}
	cropperRef.value.changeScale(zoomNum.value)
}

const handleZoom = (direction) => {
	// direction 可以是 '+' 或 '-' 来表示放大或缩小
	// cropperRef.value.getCropData((data) => {
	// 	const currentZoom = cropperRef.value.getCropBoxData().width / cropperRef.value.getImageData().naturalWidth
	// 	if (direction === '+' && currentZoom < maxZoom.value) {
	// 		cropperRef.value.zoom(zoomNum.value) // 放大0.1倍
	// 	} else if (direction === '-' && currentZoom > 1) {
	// 		cropperRef.value.zoom(zoomNum.value) // 缩小0.1倍
	// 	}
	// })
}
const handleWheel = (event) => {
	event.preventDefault() // 阻止默认的滚动行为
	const delta = event.deltaY > 0 ? -zoomNum.value : zoomNum.value // 根据滚轮方向决定缩放比例变化
	handleZoom(delta > 0 ? '-' : '+') // 调用之前定义的方法来处理缩放
}

const handleSliderInput = (val) => {
	//console.log('实时值:', val)
	// 处理滑块停止移动后的值，这里可以调整值的方向
	var val = maxValue.value - val // 使得向右滑动值变大，向左滑动值变小
	if (val <= sliderVal.value) {
		//往右
		//console.log('往右')
		zoomIn()
	} else {
		//往左
		//console.log('往左')
		zoomOut()
	}
	sliderVal.value = val
}
const handleSliderChange = (val) => {
	//console.log('最后值:', val)
	// // 处理滑块停止移动后的值，这里可以调整值的方向
	// var val = maxValue.value - val // 使得向右滑动值变大，向左滑动值变小
	// if (val <= sliderVal.value) {
	// 	//往右
	// 	console.log('往右')
	// } else {
	// 	//往左
	// 	console.log('往左')
	// }
	// sliderVal.value = val
}
//=================自定义方法=================
// const scale = ref(1)
// const rotate = ref(0)
//  // 放大
// const zoomIn = () => {
//   if (scale.value < 3) {
//     scale.value += 0.1
//     updateStyle()
//   }
// }
// // 缩小
// const zoomOut = () => {
//   if (scale.value > 0.2) {
//     scale.value -= 0.1
//     updateStyle()
//   }
// }
// // 左旋转
// const rotateLeft = () => {
//   rotate.value -= 90
//   updateStyle()
// }
// // 右旋转
// const rotateRight = () => {
//   rotate.value += 90
//   updateStyle()
// }
// // 重置
// const reset = () => {
// 	scale.value = 1
// 	rotate.value = 0
// 	updateStyle()
// }
// // 点击图片切换缩放
// const toggleZoom = () => {
// 	scale.value = scale.value === 1 ? 1.5 : 1
// 	updateStyle()
// }
// // 更新样式
// const updateStyle = () => {
// 	preview.img.value = {
// 		transform: `scale(${scale.value}) rotate(${rotate.value}deg)`,
// 		transition: 'transform 0.3s ease',
// 		transformOrigin: 'center center'
// 	}
// }
// const setPreView = (data) => {
// 	preview.w = data.w
// 	preview.h = data.h
// 	preview.div = {
// 		width: data.w + 'px',
// 		height: data.h + 'px',
// 		overflow: 'hidden',
// 		margin: '0 auto'
// 	}
// 	preview.url = data.url
// 	preview.img = {
// 		transform: `scale(${scale.value}) rotate(${rotate.value}deg)`,
// 		transition: 'transform 0.3s ease'
// 	}
// }
//=================自定义方法=================

// 获取裁剪后的blob数据

//剪裁回填数据
const getCropBlob = () => {
	cropperRef.value.getCropBlob((blob) => {
		// 这里可以处理blob数据，如上传到服务器
		// console.log('裁剪后的blob:', blob)
		//ElMessage.success('裁剪成功！')
		// 实际使用时，可以emit事件将blob传递给父组件
		emit('cropped', blob)
	})
}

// 关闭裁剪
const closeCapture = () => {
	showCaptureVisible.value = false
}

// 裁剪上传图片
const handleCropperUpload = async () => {
	//裁剪
	cropperRef.value.getCropBlob((blob) => {
		// 这里可以处理blob数据，如上传到服务器
		//console.log('裁剪后的blob:', blob)
		if (blob.size > props.fileSize * 1024 * 1024) {
			proxy.$modal.msgError('图片大小超出' + props.fileSize + '限制')
			return
		}
		let fielExt = '.jpg'
		if (imageFileType.value == 'image/png') {
			fielExt = '.png'
		}
		if (blob.type !== imageFileType.value) {
			blob = new Blob([blob], { type: imageFileType.value })
		}
		// console.log('Blob type:', blob.type) // 应该是 'image/png'
		// //方法1：在新窗口查看
		// const url = URL.createObjectURL(blob)
		// window.open(url) // 如果看到黑色背景，说明问题在前端

		const formData = new FormData()
		//追加数据
		formData.append('file', blob, 'cropped' + fielExt)
		formData.append('accountId', 0)

		// 这里调用后台api
		uploadCropperImages(formData)
			.then(
				async (res) => {
					handleUploadSuccess(res)
				},
				() => {
					proxy.$modal.msgError('图片上传失败')
				}
			)
			.finally(() => {
				// 关闭裁剪弹窗
				showCaptureVisible.value = false
			})
	}, imageFileType.value)
}
// 原图上传图片
const handleOriginalUpload = async () => {
	//console.log('原始数据:', imageSrc.value)
	//追加数据
	let data = {
		base64: imageSrc.value
	}
	// 这里调用后台api
	UploadFilesByBase64(data)
		.then(
			async (res) => {
				handleUploadSuccess(res)
			},
			() => {
				proxy.$modal.msgError('图片上传失败')
			}
		)
		.finally(() => {
			// 关闭裁剪弹窗
			showCaptureVisible.value = false
		})
}
//销毁
onBeforeUnmount(() => {
	if (cropperRef.value) {
		cropperRef.value.clearCrop()
	}
})

// 因为props.modelValue是非引用类型，改变时，需要监听，对响应式数据重新赋值
watch(
	() => props.modelValue,
	(val) => {
		if (val != null && val != '') {
			//如果是远程文件则直接赋值
			imageUrl.value = useImageUrl(val)
		} else if (imageUrl.value != null && imageUrl.value != '') {
			emit('update:modelValue', imageUrl.value)
		}
	},
	{
		immediate: true,
		deep: true
	}
)
</script>

<style scoped>
.image-cropper-container {
	width: 100%;
	max-width: 800px;
	margin: 0 auto;
}

.cropper-wrapper {
	display: flex;
	flex-wrap: wrap;
	margin-top: 20px;
}

.vue-cropper {
	width: 60%;
	height: 400px;
	background: #f0f0f0;
}

.preview-wrapper {
	width: 35%;
	margin-left: 5%;
	display: flex;
	flex-direction: column;
	align-items: center;
}

.preview-title {
	margin-bottom: 10px;
	font-weight: bold;
}

.preview-img {
	min-width: 220px;
	max-width: 220px;
	min-height: 220px;
	max-height: 220px;
	border: 1px solid #ddd;
	border-radius: 4px;
	display: flex;
	justify-content: center; /* 水平居中 */
	align-items: center; /* 垂直居中 */
}

.preview-btn {
	margin-top: 10px;
	width: 100%;
}

.action-buttons {
	margin-top: 20px;
	display: flex;
	/* justify-content: center; */
	flex-wrap: wrap;
	gap: 10px;
}
.input-upload {
	max-width: calc(100% - 120px);
	min-width: calc(100% - 140px);
	float: left;
}
.image-show {
	margin-left: 5px;
	height: 22px;
	float: left;
	.pic {
		width: 30px;
		height: 30px;
		display: inline-block;
		position: relative;
	}
}
.image-uploader {
	float: left;
	padding: 0px 8px;
}
.up-set {
	text-align: left;
	height: 30px;
	margin-bottom: 10px;
}
.up-btn {
	text-align: center;
}
.fix-input {
	width: 100%;
	float: left;
	margin: 5px 0px;
}

@media (max-width: 768px) {
	.cropper-wrapper {
		flex-direction: column;
	}

	.vue-cropper,
	.preview-wrapper {
		width: 100%;
		margin-left: 0;
	}

	.preview-wrapper {
		margin-top: 20px;
	}
}
</style>
